import * as z from 'zod/mini';
import { LENGTH_UNITS } from './cable.ts';
import {
  ACCESS_PANEL_MAX_DOORS,
  ACCESS_READER_KINDS,
  AMPLIFIER_MAX_CHANNELS,
  ANTENNA_ORIENTATIONS,
  ANTENNA_TYPES,
  AUDIO_MAX_WATTS,
  CASSETTE_MAX_PORTS,
  COMPUTER_FORM_FACTORS,
  CONVERTER_MAX_PORTS,
  CONVERTER_MIN_PORTS,
  CONVERTER_PORT_PRESETS,
  CUSTOM_TYPE_DETAIL_KEY,
  CUSTOM_VALUES_DETAIL_KEY,
  DEFAULT_PANEL_NUMBER_START,
  DEFAULT_PANEL_NUMBER_STEP,
  DEFAULT_PORT_MODE,
  DEFAULT_PORT_POE_MODE,
  DOOR_FAIL_MODES,
  DOOR_HARDWARE_KINDS,
  ENDPOINT_CONNECTIONS,
  EXTRA_KEY_MAX_LENGTH,
  EXTRA_VALUE_MAX_LENGTH,
  EXTRAS_MAX_ENTRIES,
  GATEWAY_MAX_PORTS,
  GATEWAY_PORT_PRESETS,
  ISP_NAMES,
  KEYPAD_MAX_BUTTONS,
  MOUNT_HEIGHT_MAX,
  NOTIFICATION_KINDS,
  OTHER_CHOICE,
  OUTLET_JACK_PRESETS,
  OUTLET_MAX_JACKS,
  PANEL_MAX_CASSETTES,
  PANEL_MAX_ZONES,
  PANEL_NUMBER_MAX,
  PATCH_PANEL_MAX_PORTS,
  PATCH_PANEL_PORT_PRESETS,
  PORT_CONNECTORS,
  PORT_MAX_POSITIONS,
  PORT_MODES,
  PORT_POE_MAX_WATTS,
  PORT_POE_MODES,
  PORT_DUPLEXES,
  PORT_SPEEDS,
  PORTS_MAX_ENTRIES,
  PRINTER_3D_PROCESSES,
  PRINTER_KINDS,
  PROJECTOR_MAX_LUMENS,
  RADIO_BANDS,
  RADIO_WIDTHS,
  RADIOS_MAX_ENTRIES,
  SCREEN_DIAGONAL_MAX_INCHES,
  SCREEN_MOUNTS,
  SENSOR_KINDS,
  SERVER_ROLES,
  SHORT_TEXT_MAX_LENGTH,
  SWITCH_POE_BUDGET_MAX_WATTS,
  SWITCH_PORT_COUNTS,
  VLAN_TAG_MAX,
  VLAN_TAG_MIN,
  WAN_ADDRESS_MODES,
  WANS_MAX_ENTRIES,
  WLAN_BROADCAST_MAX,
} from './constants.ts';
import { idSchema } from './schemas/common.ts';
import { customValuesSchema } from './schemas/custom-type.ts';
import { templateLayoutSchema } from './schemas/template-layout.ts';

// One registry drives everything about item types: the per-type `details`
// schema, the property form fields, and the report sections. The database
// does not repeat the list (migration 0016 dropped that check) so a new
// type is one entry here, not a table rebuild; the rack-only rule below is
// the one the database still enforces, so it is listed in the migration too.

export const ITEM_TYPES = [
  'access_point',
  'router',
  'firewall',
  'switch',
  'camera',
  'nvr',
  'ups',
  'pdu',
  'modem_ont',
  'media_converter',
  'splitter',
  'controller',
  'server_nas',
  'computer',
  'laptop',
  'screen',
  'printer_3d',
  'access_control_panel',
  'reader',
  'door_strike',
  'intrusion_panel',
  'fire_panel',
  'horn_strobe',
  'amplifier',
  'receiver',
  'projector',
  'speaker',
  'keypad',
  'thermostat',
  'sensor',
  'phone',
  'printer',
  'wall_patch_panel',
  'outlet',
  'other',
  'patch_panel',
  'blank_panel',
  'shelf',
  'drawer',
  'cable_manager',
  'power_strip',
] as const;
export type ItemType = (typeof ITEM_TYPES)[number];
export const itemTypeSchema = z.enum(ITEM_TYPES);

/** Types that only make sense mounted in a rack. */
export const RACK_ONLY_TYPES: ReadonlySet<ItemType> = new Set<ItemType>([
  'patch_panel',
  'blank_panel',
  'shelf',
  'drawer',
  'cable_manager',
  'power_strip',
]);

export function isRackOnly(type: ItemType): boolean {
  return RACK_ONLY_TYPES.has(type);
}

/**
 * Patch panels, racked or on a wall: numbered ports with two sides, where a
 * run lands on the rear and a patch cord plugs into the front.
 */
export const PANEL_TYPES: ReadonlySet<ItemType> = new Set<ItemType>([
  'patch_panel',
  'wall_patch_panel',
]);

export function isPanel(type: ItemType): boolean {
  return PANEL_TYPES.has(type);
}

/**
 * Devices a technician expects to reach by address: the dashboard counts
 * these when they have no IP recorded. Endpoints (a computer, a phone) are
 * left out, since they usually take whatever DHCP gives them.
 */
export const ADDRESSED_TYPES: ReadonlySet<ItemType> = new Set<ItemType>([
  'modem_ont',
  'router',
  'firewall',
  'switch',
  'access_point',
  'controller',
  'nvr',
  'camera',
  'server_nas',
  'printer',
  // A 3D printer sits on the network under its own address the way any other
  // printer does (ROADMAP J8); a screen and a laptop take what DHCP gives them.
  'printer_3d',
  // The three security panels are reached by address for their own software
  // (ROADMAP J7); a reader, a strike, and a horn hang off their panel's bus.
  'access_control_panel',
  'intrusion_panel',
  'fire_panel',
]);

/** Rack parts that hold items of their own (drawn inside their block). */
export const CONTAINER_TYPES: ReadonlySet<ItemType> = new Set<ItemType>(['shelf', 'drawer']);

export function isContainer(type: ItemType): boolean {
  return CONTAINER_TYPES.has(type);
}

/** The detail key holding the WLANs an access point broadcasts (ROADMAP G2). */
export const WLAN_BROADCAST_FIELD = 'ssids';
/** The detail key holding the WLAN a client device's wireless port joined (G2). */
export const WIRELESS_WLAN_FIELD = 'wirelessWlan';

export type FieldKind =
  | 'text'
  | 'number'
  | 'choice'
  | 'multi'
  | 'boolean'
  | 'ports'
  | 'wans'
  | 'radios'
  | 'cassettes'
  /** The WLANs a device broadcasts: a list of ids from the client's table (G2). */
  | 'wlans'
  /** The one WLAN a device's wireless port has joined (G2). */
  | 'wlan';

export interface FieldDefinition {
  readonly key: string;
  readonly label: string;
  readonly kind: FieldKind;
  /** For `choice` (a dropdown) and `multi` (checkboxes, several at once): the values. */
  readonly options?: readonly (number | string)[];
  /** For a numeric `choice`: the word after the number ("24 ports"). */
  readonly unit?: string;
  /**
   * For `number`: the bounds the schema enforces; with both set the field is
   * a stepper. A bounded number is a whole one (a count of ports, a wattage).
   */
  readonly min?: number;
  readonly max?: number;
  /** For `number`: values offered as one-tap picks before typing anything. */
  readonly presets?: readonly number[];
  /** Shown (and meaningful) only while this boolean field is set. */
  readonly when?: { readonly key: string; readonly equals: boolean | string };
  /** A `choice` that must have a value: no "Not set", and it defaults to the first option. */
  readonly required?: boolean;
}

export interface ItemTypeDefinition {
  readonly label: string;
  readonly fields: readonly FieldDefinition[];
}

const text = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'text' });
const number = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'number' });
/** A whole number that may not exceed `max`, typed rather than stepped. */
const bounded = (key: string, label: string, max: number): FieldDefinition => ({
  key,
  label,
  kind: 'number',
  max,
});
const count = (
  key: string,
  label: string,
  min: number,
  max: number,
  presets: readonly number[],
): FieldDefinition => ({ key, label, kind: 'number', min, max, presets });
const choice = (
  key: string,
  label: string,
  options: readonly (number | string)[],
  unit?: string,
): FieldDefinition => ({
  key,
  label,
  kind: 'choice',
  options,
  ...(unit === undefined ? {} : { unit }),
});
/** A choice that must be set: no "Not set" option, defaults to the first value. */
const requiredChoice = (
  key: string,
  label: string,
  options: readonly (number | string)[],
  unit?: string,
): FieldDefinition => ({ ...choice(key, label, options, unit), required: true });

const boolean = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'boolean' });
const multi = (key: string, label: string, options: readonly string[]): FieldDefinition => ({
  key,
  label,
  kind: 'multi',
  options,
});
const ports = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'ports' });
const wans = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'wans' });
const radios = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'radios' });
/** A panel's swappable modules (ROADMAP I3); edited on the ports screen, like `ports`. */
const cassettes = (key: string, label: string): FieldDefinition => ({
  key,
  label,
  kind: 'cassettes',
});
/** The WLANs an access point broadcasts (ROADMAP G2); ids from the client's table. */
const wlans = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'wlans' });
/** The WLAN a client device's wireless port has joined (ROADMAP G2). */
const wlan = (key: string, label: string): FieldDefinition => ({ key, label, kind: 'wlan' });
const onlyWhen = (
  field: FieldDefinition,
  key: string,
  equals: boolean | string = true,
): FieldDefinition => ({ ...field, when: { key, equals } });

/**
 * What a Wi-Fi radio adds to a device: the access point's own fields. Each
 * radio (band, channel, width, power) is a row; migration 0029 turned the
 * old single "channel" text into the first row. The mounting fields
 * (ROADMAP J3) are what a survey asks for — where it hangs, how high in
 * whichever unit the site is measured in, and which antenna faces which way
 * — and read as one line through `describeMounting`; none of them is
 * required, so an access point recorded before J3 reads exactly as before.
 */
const WIFI_FIELDS: readonly FieldDefinition[] = [
  wlans(WLAN_BROADCAST_FIELD, 'WLANs broadcast'),
  text('mountLocation', 'Mount location'),
  bounded('mountHeight', 'Mount height', MOUNT_HEIGHT_MAX),
  // The unit is an ordinary choice, not a required one: a required choice
  // carries its first value into every device of the type, so every router
  // with the Wi-Fi switch off would store a height unit for a height it has
  // not got. Left unset with a height, the line says the unit is missing.
  choice('mountHeightUnit', 'Height unit', LENGTH_UNITS),
  choice('antennaType', 'Antenna', ANTENNA_TYPES),
  choice('antennaOrientation', 'Antenna orientation', ANTENNA_ORIENTATIONS),
  radios('radios', 'Radios'),
];

/**
 * The wireless port of a client device (ROADMAP G2): the WLAN a camera, a
 * laptop, a phone, or a printer joined, chosen from its client's table. Left
 * unset, the device is wired (or nobody has recorded how it connects), which
 * is how every device recorded before this reads.
 */
const WIRELESS_CLIENT_FIELDS: readonly FieldDefinition[] = [
  wlan(WIRELESS_WLAN_FIELD, 'Wireless port (WLAN)'),
];

/**
 * What a general-purpose computer records (ROADMAP J8). A laptop carries the
 * same facts as a desktop — the shape of the box is the type, not a field —
 * so the two share this list and `computer` adds its form factor on top,
 * which keeps every desktop recorded before J8 reading exactly as before.
 */
const COMPUTER_FIELDS: readonly FieldDefinition[] = [
  text('hostname', 'Hostname'),
  text('os', 'Operating system'),
  text('assignedTo', 'Assigned to'),
  choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
  ...WIRELESS_CLIENT_FIELDS,
];

/**
 * What every patch panel records: how many ports, the cassettes it is built
 * of (ROADMAP I3), how its ports are numbered and laid out (ROADMAP I4), and
 * what each port is. A panel with cassettes takes its capacity from their
 * ports together; a flat panel keeps its `portCount`, so a panel recorded
 * before any of this reads exactly as before.
 */
const PANEL_FIELDS: readonly FieldDefinition[] = [
  count('portCount', 'Port count', 1, PATCH_PANEL_MAX_PORTS, PATCH_PANEL_PORT_PRESETS),
  cassettes('cassettes', 'Cassettes'),
  count('numberStart', 'First port number', 0, PANEL_NUMBER_MAX, [DEFAULT_PANEL_NUMBER_START]),
  count('numberStep', 'Number step', 1, PANEL_NUMBER_MAX, [DEFAULT_PANEL_NUMBER_STEP]),
  boolean('numberByCassette', 'Number within each cassette'),
  boolean('rearMirrorsFront', 'Rear numbered opposite the front'),
  ports('ports', 'Ports'),
];

/**
 * The LAN side of a gateway: its ports, so a link can land on one the way
 * it does on a switch. Left blank, the device links without a port.
 */
const GATEWAY_PORT_FIELDS: readonly FieldDefinition[] = [
  count('portCount', 'LAN ports', 1, GATEWAY_MAX_PORTS, GATEWAY_PORT_PRESETS),
  ports('ports', 'Ports'),
];

/** The detail keys holding the two connector sides a converter mates (ROADMAP J5). */
export const SIDE_A_CONNECTOR_FIELD = 'sideAConnector';
export const SIDE_B_CONNECTOR_FIELD = 'sideBConnector';

/**
 * A media converter or a passive splitter (ROADMAP J5): a device whose two
 * sides present different connectors, so a run changes medium inside it. Port
 * 1 is the A side — the copper jack of a converter, the input of a splitter —
 * and every higher port is a leg of the B side, which is why one port count
 * covers both shapes. The connectors are declared per side rather than per
 * port: a splitter's legs are all the same.
 */
const CONVERTER_FIELDS: readonly FieldDefinition[] = [
  choice(SIDE_A_CONNECTOR_FIELD, 'A side connector', PORT_CONNECTORS),
  choice(SIDE_B_CONNECTOR_FIELD, 'B side connector', PORT_CONNECTORS),
  count(
    'portCount',
    'Port count',
    CONVERTER_MIN_PORTS,
    CONVERTER_MAX_PORTS,
    CONVERTER_PORT_PRESETS,
  ),
  ports('ports', 'Ports'),
];

/**
 * A telecom outlet (wall plate, ROADMAP D6): a faceplate with one or more
 * jacks. Each jack is a numbered port a run lands on and a patch consumes, so
 * the jacks are the same `ports` a panel or switch records; a plan outlet
 * marker names one of these items.
 */
const OUTLET_FIELDS: readonly FieldDefinition[] = [
  text('faceplate', 'Faceplate label'),
  count('portCount', 'Jack count', 1, OUTLET_MAX_JACKS, OUTLET_JACK_PRESETS),
  ports('ports', 'Jacks'),
];

export const ITEM_TYPE_DEFINITIONS: Readonly<Record<ItemType, ItemTypeDefinition>> = {
  access_point: {
    label: 'Access point',
    fields: [...WIFI_FIELDS, boolean('poe', 'PoE powered')],
  },
  router: {
    label: 'Router',
    fields: [
      wans('wans', 'WAN links'),
      ...GATEWAY_PORT_FIELDS,
      text('lanSubnets', 'LAN subnets'),
      text('vlans', 'VLANs'),
      boolean('wifi', 'Has Wi-Fi'),
      ...WIFI_FIELDS.map((field) => onlyWhen(field, 'wifi')),
    ],
  },
  firewall: {
    label: 'Firewall',
    fields: [
      wans('wans', 'WAN links'),
      ...GATEWAY_PORT_FIELDS,
      text('lanSubnets', 'LAN subnets'),
      text('vlans', 'VLANs'),
      text('vpn', 'VPN'),
    ],
  },
  switch: {
    label: 'Switch',
    fields: [
      requiredChoice('portCount', 'Port count', SWITCH_PORT_COUNTS, 'ports'),
      boolean('poe', 'PoE'),
      bounded('poeBudgetWatts', 'PoE budget (W)', SWITCH_POE_BUDGET_MAX_WATTS),
      boolean('managed', 'Managed'),
      ports('ports', 'Ports'),
    ],
  },
  camera: {
    label: 'Camera',
    fields: [
      text('resolution', 'Resolution'),
      boolean('poe', 'PoE powered'),
      text('nvrChannel', 'NVR channel'),
      text('fieldOfView', 'Field of view'),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  nvr: {
    label: 'NVR',
    fields: [
      number('channelCount', 'Channel count'),
      text('storage', 'Storage'),
      number('retentionDays', 'Retention (days)'),
    ],
  },
  ups: {
    label: 'UPS',
    fields: [
      number('va', 'VA rating'),
      number('outlets', 'Outlets'),
      number('runtimeMinutes', 'Runtime (minutes)'),
    ],
  },
  pdu: { label: 'PDU', fields: [number('outlets', 'Outlets'), text('amperage', 'Amperage')] },
  modem_ont: {
    label: 'Modem / ONT',
    fields: [
      choice('isp', 'ISP', ISP_NAMES),
      onlyWhen(text('ispName', 'ISP name'), 'isp', OTHER_CHOICE),
      text('accountNumber', 'Account number'),
      text('wanIp', 'WAN IP'),
      ...GATEWAY_PORT_FIELDS,
    ],
  },
  media_converter: {
    label: 'Media converter',
    fields: [...CONVERTER_FIELDS, text('dataRate', 'Data rate')],
  },
  splitter: {
    label: 'Splitter',
    fields: [...CONVERTER_FIELDS, text('insertionLoss', 'Insertion loss (dB)')],
  },
  controller: {
    label: 'Controller / hub',
    fields: [text('manages', 'Manages'), text('managementUrl', 'Management URL')],
  },
  server_nas: {
    label: 'Server / NAS',
    fields: [
      multi('roles', 'Roles', SERVER_ROLES),
      text('os', 'Operating system'),
      text('storage', 'Storage'),
      text('services', 'Services'),
    ],
  },
  computer: {
    label: 'Computer',
    fields: [choice('formFactor', 'Form factor', COMPUTER_FORM_FACTORS), ...COMPUTER_FIELDS],
  },
  laptop: { label: 'Laptop', fields: [...COMPUTER_FIELDS, text('dock', 'Dock')] },
  screen: {
    label: 'Screen / display',
    fields: [
      bounded('diagonalInches', 'Diagonal (in)', SCREEN_DIAGONAL_MAX_INCHES),
      text('resolution', 'Resolution'),
      choice('screenMount', 'Mount', SCREEN_MOUNTS),
      text('inputs', 'Inputs'),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  printer_3d: {
    label: '3D printer',
    fields: [
      choice('printProcess', 'Process', PRINTER_3D_PROCESSES),
      text('buildVolume', 'Build volume'),
      text('materials', 'Materials'),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
      text('queueName', 'Queue / share name'),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  // Security and life safety (ROADMAP J7). Every one of them names the zone
  // it belongs to, which is a record of the client (RECORD_KINDS 'zone'), so
  // one drawing's zone list and the devices in it agree.
  access_control_panel: {
    label: 'Access control panel',
    fields: [
      bounded('doors', 'Doors', ACCESS_PANEL_MAX_DOORS),
      text('manages', 'Manages'),
      text('managementUrl', 'Management URL'),
      text('zone', 'Zone'),
      boolean('poe', 'PoE powered'),
    ],
  },
  reader: {
    label: 'Reader',
    fields: [
      choice('readerKind', 'Kind', ACCESS_READER_KINDS),
      text('door', 'Door'),
      text('protocol', 'Protocol'),
      text('zone', 'Zone'),
      boolean('poe', 'PoE powered'),
    ],
  },
  door_strike: {
    label: 'Door strike / lock',
    fields: [
      choice('lockKind', 'Hardware', DOOR_HARDWARE_KINDS),
      choice('failMode', 'Fail mode', DOOR_FAIL_MODES),
      text('door', 'Door'),
      text('voltage', 'Voltage'),
      text('zone', 'Zone'),
    ],
  },
  intrusion_panel: {
    label: 'Intrusion panel',
    fields: [
      bounded('zoneCount', 'Zones', PANEL_MAX_ZONES),
      text('zones', 'Zones served'),
      text('communicator', 'Communicator'),
      text('monitoredBy', 'Monitored by'),
      text('managementUrl', 'Management URL'),
    ],
  },
  fire_panel: {
    label: 'Fire panel',
    fields: [
      bounded('zoneCount', 'Zones', PANEL_MAX_ZONES),
      text('zones', 'Zones served'),
      text('communicator', 'Communicator'),
      text('monitoredBy', 'Monitored by'),
      text('inspectionDue', 'Inspection due'),
    ],
  },
  horn_strobe: {
    label: 'Horn / strobe',
    fields: [
      choice('notificationKind', 'Kind', NOTIFICATION_KINDS),
      text('candela', 'Candela'),
      text('circuit', 'Notification circuit'),
      text('zone', 'Zone'),
    ],
  },
  amplifier: {
    label: 'Amplifier',
    fields: [
      bounded('channels', 'Channels', AMPLIFIER_MAX_CHANNELS),
      bounded('powerWatts', 'Power per channel (W)', AUDIO_MAX_WATTS),
      text('zones', 'Zones'),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
    ],
  },
  receiver: {
    label: 'Receiver / processor',
    fields: [
      text('inputs', 'Inputs'),
      text('outputs', 'Outputs'),
      text('zones', 'Zones'),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
    ],
  },
  projector: {
    label: 'Projector',
    fields: [
      text('resolution', 'Resolution'),
      bounded('lumens', 'Brightness (lumens)', PROJECTOR_MAX_LUMENS),
      text('throwDistance', 'Throw distance'),
      choice('screenMount', 'Mount', SCREEN_MOUNTS),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  speaker: {
    label: 'Speaker',
    fields: [
      text('impedance', 'Impedance'),
      bounded('powerWatts', 'Power (W)', AUDIO_MAX_WATTS),
      text('zone', 'Zone'),
      choice('screenMount', 'Mount', SCREEN_MOUNTS),
    ],
  },
  keypad: {
    label: 'Keypad',
    fields: [
      bounded('buttons', 'Buttons', KEYPAD_MAX_BUTTONS),
      text('controls', 'Controls'),
      text('zone', 'Zone'),
    ],
  },
  thermostat: {
    label: 'Thermostat',
    fields: [
      text('system', 'System served'),
      text('zone', 'Zone'),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  sensor: {
    label: 'Sensor',
    fields: [
      choice('sensorKind', 'Kind', SENSOR_KINDS),
      text('zone', 'Zone'),
      text('covers', 'Covers'),
      boolean('poe', 'PoE powered'),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  phone: {
    label: 'Phone',
    fields: [
      text('extension', 'Extension'),
      text('pbx', 'PBX / SIP server'),
      text('assignedTo', 'Assigned to'),
      boolean('poe', 'PoE powered'),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  printer: {
    label: 'Printer',
    fields: [
      choice('printerKind', 'Kind', PRINTER_KINDS),
      boolean('color', 'Color'),
      choice('connection', 'Connection', ENDPOINT_CONNECTIONS),
      text('queueName', 'Queue / share name'),
      ...WIRELESS_CLIENT_FIELDS,
    ],
  },
  wall_patch_panel: {
    label: 'Wall-mount patch panel',
    fields: PANEL_FIELDS,
  },
  outlet: {
    label: 'Telecom outlet',
    fields: OUTLET_FIELDS,
  },
  other: { label: 'Other', fields: [] },
  patch_panel: {
    label: 'Patch panel',
    fields: PANEL_FIELDS,
  },
  blank_panel: { label: 'Blank panel', fields: [number('sizeU', 'Size (U)')] },
  shelf: { label: 'Shelf', fields: [number('sizeU', 'Size (U)')] },
  drawer: { label: 'Drawer', fields: [number('sizeU', 'Size (U)')] },
  cable_manager: { label: 'Cable manager', fields: [number('sizeU', 'Size (U)')] },
  power_strip: { label: 'Power strip', fields: [number('outlets', 'Outlets')] },
};

/** A port's physical connector (B2); a subset of these mate, see connectorsMate. */
export type PortConnector = (typeof PORT_CONNECTORS)[number];
export type PortMode = (typeof PORT_MODES)[number];
export type PortPoeMode = (typeof PORT_POE_MODES)[number];

export const portSchema = z.object({
  number: z.int().check(z.nonnegative()),
  label: z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH)),
  vlan: z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH)),
  connectedTo: z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH)),
  poe: z.boolean(),
  /** What the port supplies in watts; left out (ports recorded before it existed, or blank) means none. */
  poeWatts: z.optional(z.number().check(z.nonnegative(), z.lte(PORT_POE_MAX_WATTS))),
  /** The physical connector (B2); left out for a port recorded before it, or when not chosen. */
  connector: z.optional(z.enum(PORT_CONNECTORS)),
  /** The link speed (C2); left out when not recorded. */
  speed: z.optional(z.enum(PORT_SPEEDS)),
  /** The duplex (C2): auto, full, or half; left out when not recorded. */
  duplex: z.optional(z.enum(PORT_DUPLEXES)),
  /** Whether the port is enabled (C2); left out means enabled, so an old port stays live. */
  enabled: z.optional(z.boolean()),
  /** access or trunk (C3); left out means access, so `vlan` keeps its old meaning as the access tag. */
  mode: z.optional(z.enum(PORT_MODES)),
  /** The tagged VLANs a trunk carries, as typed ("20, 30, 40"); left out on an access port. */
  taggedVlans: z.optional(z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH))),
  /** The PoE mode (C4); left out falls back to the `poe` boolean (true → auto, false → off). */
  poeMode: z.optional(z.enum(PORT_POE_MODES)),
  /**
   * How many positions this port fans out to (ROADMAP I2): an MPO/breakout
   * rear port carries several legs, each patched on its own position. Left out
   * (or 1) means a plain single-jack port, so every port recorded before I2
   * reads unchanged; a link then lands on the whole port, not a position.
   */
  positions: z.optional(z.int().check(z.gte(1), z.lte(PORT_MAX_POSITIONS))),
});
export type Port = z.infer<typeof portSchema>;

/** How many positions a port fans out to; 1 (a plain jack) when it declares none. */
export function portPositions(port: Pick<Port, 'positions'>): number {
  return port.positions ?? 1;
}

/** What a port reads for whichever new field, treating an absent value as its default. */
export function portPoeMode(port: Pick<Port, 'poe' | 'poeMode'>): PortPoeMode {
  return port.poeMode ?? (port.poe ? 'auto' : DEFAULT_PORT_POE_MODE);
}

/** Whether a port is enabled; a port recorded before C2 (no flag) is enabled. */
export function portEnabled(port: Pick<Port, 'enabled'>): boolean {
  return port.enabled !== false;
}

/** A port's VLAN mode; a port recorded before C3 (no mode) is an access port. */
export function portMode(port: Pick<Port, 'mode'>): PortMode {
  return port.mode ?? DEFAULT_PORT_MODE;
}

/**
 * The mating family of a connector (B3): copper (an RJ45 or a console/USB
 * jack), fiber (LC, SC, and the rest of the optical connectors), or a cage
 * (SFP and its kin) that takes a transceiver of either medium and so mates
 * with anything. Two ports mate when their families are the same or one is a
 * cage; a copper jack and a fiber connector cannot, which is the rule B3 adds.
 */
export type ConnectorFamily = 'copper' | 'fiber' | 'cage';

const CONNECTOR_FAMILIES: Readonly<Record<PortConnector, ConnectorFamily>> = {
  RJ45: 'copper',
  console: 'copper',
  USB: 'copper',
  power: 'copper',
  SFP: 'cage',
  'SFP+': 'cage',
  SFP28: 'cage',
  QSFP: 'cage',
  LC: 'fiber',
  SC: 'fiber',
  E2000: 'fiber',
  ST: 'fiber',
  MPO: 'fiber',
};

export function connectorFamily(connector: PortConnector): ConnectorFamily {
  return CONNECTOR_FAMILIES[connector];
}

/**
 * Whether two connectors can be joined by a single cable. A cage (SFP …)
 * mates with anything because a matching transceiver adapts it; otherwise
 * the families must match, so RJ45↔LC (copper↔fiber) is refused while
 * RJ45↔RJ45 and LC↔SC are fine.
 */
export function connectorsMate(a: PortConnector, b: PortConnector): boolean {
  const fa = connectorFamily(a);
  const fb = connectorFamily(b);
  return fa === 'cage' || fb === 'cage' || fa === fb;
}

/**
 * Device types that convert one medium to another (a modem/ONT turns the
 * ISP's fiber into ethernet), so a link through one may join connectors that
 * would not otherwise mate. Used by the connector rule in shared/connections.ts.
 * Since ROADMAP J5 the purpose-built pair joins it: a media converter and a
 * passive splitter declare the two sides they mate, and a trace passes
 * through them.
 */
export const MEDIA_CONVERTER_TYPES: ReadonlySet<ItemType> = new Set<ItemType>([
  'modem_ont',
  'media_converter',
  'splitter',
]);

export function isMediaConverter(type: ItemType): boolean {
  return MEDIA_CONVERTER_TYPES.has(type);
}

/** Which side of a converter a port sits on (ROADMAP J5): port 1 is A, the rest are B. */
export type ConverterSide = 'a' | 'b';

/** The A side is one port; a splitter's legs are the B side. */
const CONVERTER_A_SIDE_PORT = 1;

export function converterSideOfPort(port: number): ConverterSide {
  return port === CONVERTER_A_SIDE_PORT ? 'a' : 'b';
}

/** The connector a converter's side presents, or null when that side is not declared. */
export function converterSideConnector(
  details: Readonly<Record<string, unknown>>,
  side: ConverterSide,
): PortConnector | null {
  const value = details[side === 'a' ? SIDE_A_CONNECTOR_FIELD : SIDE_B_CONNECTOR_FIELD];
  const parsed = z.safeParse(z.enum(PORT_CONNECTORS), value);
  return parsed.success ? parsed.data : null;
}

/**
 * Whether a converter has said what it mates: both sides declared. Until it
 * has, the connector rule leaves its links alone, since a device that bridges
 * media on purpose and has not said how cannot be judged.
 */
export function declaresConverterSides(details: Readonly<Record<string, unknown>>): boolean {
  return (
    converterSideConnector(details, 'a') !== null && converterSideConnector(details, 'b') !== null
  );
}

/** The connector a converter presents at one of its numbered ports (ROADMAP J5). */
export function converterConnectorAt(
  details: Readonly<Record<string, unknown>>,
  port: number,
): PortConnector | null {
  return converterSideConnector(details, converterSideOfPort(port));
}

const portsSchema = z.array(portSchema).check(z.maxLength(PORTS_MAX_ENTRIES));

/** A stored `ports` value: rows with the port shape (the schema has already checked them). */
export function isPortList(value: unknown): value is readonly Port[] {
  return (
    Array.isArray(value) &&
    value.every((row: unknown) => typeof row === 'object' && row !== null && 'poe' in row)
  );
}

const shortText = z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH));

/**
 * One cassette (module) of a modular patch panel (ROADMAP I3): a swappable
 * block with its own ports, and optionally its own connector, cable type,
 * length, and colour. A panel's ports are its cassettes' ports together, in
 * cassette order, numbered by the panel's layout (ROADMAP I4). A cassette's
 * `id` is a stable slot key so a rename or reorder keeps its ports.
 */
/** An optional port connector (B2), shared by the cassette schema. */
const optionalConnector = z.optional(z.enum(PORT_CONNECTORS));

export const cassetteSchema = z.object({
  id: shortText,
  name: shortText,
  portCount: z.int().check(z.gte(1), z.lte(CASSETTE_MAX_PORTS)),
  /** The connector every port of the cassette presents; left out when mixed or unset. */
  connector: optionalConnector,
});
export type Cassette = z.infer<typeof cassetteSchema>;

const cassettesSchema = z.array(cassetteSchema).check(z.maxLength(PANEL_MAX_CASSETTES));

/** A stored `cassettes` value: rows with the cassette shape. */
export function isCassetteList(value: unknown): value is readonly Cassette[] {
  return (
    Array.isArray(value) &&
    value.every(
      (row: unknown) =>
        typeof row === 'object' && row !== null && 'portCount' in row && 'id' in row,
    )
  );
}

/** The cassettes a panel is built of, or an empty list for a flat panel (ROADMAP I3). */
export function panelCassettes(details: Readonly<Record<string, unknown>>): readonly Cassette[] {
  const stored = details['cassettes'];
  return isCassetteList(stored) ? stored : [];
}

/** The ports a set of cassettes offers together: the sum of their port counts. */
export function cassettePortCount(cassettes: readonly Cassette[]): number {
  return cassettes.reduce((sum, cassette) => sum + cassette.portCount, 0);
}

/** A panel's port count from its cassettes (ROADMAP I3), or null when it has none. */
export function panelCassetteCapacity(details: Readonly<Record<string, unknown>>): number | null {
  const cassettes = panelCassettes(details);
  return cassettes.length === 0 ? null : cassettePortCount(cassettes);
}

/** Which cassette a 1-based physical port falls in, and where within it; null past the last. */
export function cassetteOfPort(
  cassettes: readonly Cassette[],
  port: number,
): { readonly cassette: Cassette; readonly ordinal: number; readonly within: number } | null {
  let base = 0;
  for (const [index, cassette] of cassettes.entries()) {
    if (port > base && port <= base + cassette.portCount) {
      return { cassette, ordinal: index + 1, within: port - base };
    }
    base += cassette.portCount;
  }
  return null;
}

/** How a panel numbers and lays out its ports (ROADMAP I4), each field at its default. */
export interface PanelNumbering {
  /** The number the first port carries. */
  readonly start: number;
  /** How much the number climbs from one port to the next. */
  readonly step: number;
  /** Whether each cassette's ports restart the count under the cassette's name. */
  readonly byCassette: boolean;
  /** Whether the rear is numbered opposite the front (port 1 rear faces the last front). */
  readonly rearMirrorsFront: boolean;
}

/** A panel's numbering, reading each field's default when it is not recorded. */
export function panelNumbering(details: Readonly<Record<string, unknown>>): PanelNumbering {
  const num = (key: string, fallback: number): number => {
    const value = details[key];
    return typeof value === 'number' ? value : fallback;
  };
  return {
    start: num('numberStart', DEFAULT_PANEL_NUMBER_START),
    step: num('numberStep', DEFAULT_PANEL_NUMBER_STEP),
    byCassette: details['numberByCassette'] === true,
    rearMirrorsFront: details['rearMirrorsFront'] === true,
  };
}

/** The number shown for a physical (1-based) port, following the panel's numbering (ROADMAP I4). */
export function displayedPortNumber(
  details: Readonly<Record<string, unknown>>,
  port: number,
): number {
  const numbering = panelNumbering(details);
  const cassettes = panelCassettes(details);
  if (numbering.byCassette && cassettes.length > 0) {
    const found = cassetteOfPort(cassettes, port);
    if (found !== null) {
      return numbering.start + (found.within - 1) * numbering.step;
    }
  }
  return numbering.start + (port - 1) * numbering.step;
}

/**
 * The label shown for a physical port (ROADMAP I4): the numbered form, or
 * "Cassette A 3" when the panel numbers within each cassette (ROADMAP I3).
 */
export function portLabel(details: Readonly<Record<string, unknown>>, port: number): string {
  const numbering = panelNumbering(details);
  const cassettes = panelCassettes(details);
  const number = displayedPortNumber(details, port);
  if (numbering.byCassette && cassettes.length > 0) {
    const found = cassetteOfPort(cassettes, port);
    if (found !== null) {
      const name =
        found.cassette.name === '' ? `Cassette ${String(found.ordinal)}` : found.cassette.name;
      return `${name} ${String(number)}`;
    }
  }
  return String(number);
}

/**
 * The physical rear port that faces a given front port (ROADMAP I4): the same
 * port, unless the panel is numbered with the rear mirrored, when port 1 at
 * the front faces the last port at the rear.
 */
export function rearFacingPort(
  details: Readonly<Record<string, unknown>>,
  capacity: number,
  frontPort: number,
): number {
  return panelNumbering(details).rearMirrorsFront ? capacity + 1 - frontPort : frontPort;
}

/**
 * One radio of a Wi-Fi device: which band, the channel it is on, how wide,
 * and its transmit power as written on the controller ("20 dBm", "Auto").
 */
export const radioSchema = z.object({
  band: z.enum(['', ...RADIO_BANDS]),
  channel: shortText,
  width: z.enum(['', ...RADIO_WIDTHS]),
  txPower: shortText,
});
export type Radio = z.infer<typeof radioSchema>;

const radiosSchema = z.array(radioSchema).check(z.maxLength(RADIOS_MAX_ENTRIES));

/** An 802.1Q tag as typed: blank for untagged, else a number in range. */
export const vlanTagSchema = z.string().check(
  z.regex(/^\d{0,4}$/u, 'Digits only'),
  z.refine(
    (tag) => tag === '' || (Number(tag) >= VLAN_TAG_MIN && Number(tag) <= VLAN_TAG_MAX),
    `A VLAN tag is ${String(VLAN_TAG_MIN)}–${String(VLAN_TAG_MAX)}`,
  ),
);

/**
 * One WAN link of a gateway. A multi-WAN router lists one per ISP handoff;
 * the VLAN tag is the one the handoff expects on that link, kept beside the
 * address it applies to.
 */
export const wanSchema = z.object({
  label: shortText,
  isp: shortText,
  mode: z.enum(['', ...WAN_ADDRESS_MODES]),
  ipAddress: shortText,
  gateway: shortText,
  vlan: vlanTagSchema,
  interfaceName: shortText,
  /** The ISP account and the number to ring about it; left out on links recorded before they existed. */
  accountNumber: z.optional(shortText),
  supportPhone: z.optional(shortText),
  /**
   * The ISP circuit this link lands on (ROADMAP G6), by id from its client's
   * table; '' or left out when the service is not recorded as a circuit. The
   * link keeps its own address fields: the address is the link's, the service
   * is the circuit's.
   */
  circuitId: z.optional(z.union([z.literal(''), idSchema])),
});
export type Wan = z.infer<typeof wanSchema>;

const wansSchema = z.array(wanSchema).check(z.maxLength(WANS_MAX_ENTRIES));

/**
 * The WLANs an access point broadcasts: ids from its client's table (ROADMAP
 * G2, migration 0058 turned the old free-text SSID list into these). An id
 * that names no WLAN is not storable, since the form only offers the client's
 * own rows and removing a WLAN takes its references with it.
 */
const wlanIdsSchema = z.array(idSchema).check(z.maxLength(WLAN_BROADCAST_MAX));

/** The WLAN a wireless port joined, or '' when it has joined none. */
const wlanIdSchema = z.union([z.literal(''), idSchema]);

/** A stored `wlans` value: a list of WLAN ids (the schema has already checked them). */
export function isWlanList(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry: unknown) => typeof entry === 'string');
}

/** The WLANs a device broadcasts, in the order it lists them; none when it broadcasts nothing. */
export function broadcastWlanIds(details: Readonly<Record<string, unknown>>): readonly string[] {
  const value = details[WLAN_BROADCAST_FIELD];
  return isWlanList(value) ? value : [];
}

/** The WLAN a device's wireless port joined, or '' when it has joined none. */
export function joinedWlanId(details: Readonly<Record<string, unknown>>): string {
  const value = details[WIRELESS_WLAN_FIELD];
  return typeof value === 'string' ? value : '';
}

/** A choice whose options are words rather than sizes. */
export function isTextChoice(field: FieldDefinition): boolean {
  return (
    field.kind === 'choice' && (field.options ?? []).every((option) => typeof option === 'string')
  );
}

function fieldSchema(field: FieldDefinition) {
  switch (field.kind) {
    case 'text': {
      return z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH));
    }
    case 'number': {
      return field.max === undefined
        ? z.number().check(z.nonnegative())
        : z.int().check(z.gte(field.min ?? 0), z.lte(field.max));
    }
    case 'choice': {
      return isTextChoice(field)
        ? z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH))
        : z.number().check(z.nonnegative());
    }
    case 'multi': {
      return z
        .array(z.string().check(z.maxLength(SHORT_TEXT_MAX_LENGTH)))
        .check(z.maxLength((field.options ?? []).length));
    }
    case 'boolean': {
      return z.boolean();
    }
    case 'ports': {
      return portsSchema;
    }
    case 'wans': {
      return wansSchema;
    }
    case 'radios': {
      return radiosSchema;
    }
    case 'cassettes': {
      return cassettesSchema;
    }
    case 'wlans': {
      return wlanIdsSchema;
    }
    case 'wlan': {
      return wlanIdSchema;
    }
  }
}

/** The fields that apply given the current details (conditional ones only when their switch is on). */
export function activeFields(
  type: ItemType,
  details: Readonly<Record<string, unknown>>,
): FieldDefinition[] {
  return ITEM_TYPE_DEFINITIONS[type].fields.filter(
    (field) => field.when === undefined || details[field.when.key] === field.when.equals,
  );
}

/** Whether a device has a Wi-Fi radio: access points always, routers when switched on. */
export function hasWifi(type: ItemType, details: Readonly<Record<string, unknown>>): boolean {
  return type === 'access_point' || (type === 'router' && details['wifi'] === true);
}

/**
 * The `details` object for one item type: every field optional, unknown
 * keys rejected so a typo cannot silently store an unrelated value.
 */
export function detailsSchemaFor(type: ItemType) {
  const shape: Record<string, ReturnType<typeof fieldSchema>> = {};
  for (const field of ITEM_TYPE_DEFINITIONS[type].fields) {
    shape[field.key] = fieldSchema(field);
  }
  return z.strictObject({
    ...z.partial(z.object(shape)).shape,
    [CUSTOM_TYPE_DETAIL_KEY]: z.optional(idSchema),
    [CUSTOM_VALUES_DETAIL_KEY]: z.optional(customValuesSchema),
    templateLayout: z.optional(templateLayoutSchema),
  });
}

/** What the per-type `details` check needs of whatever it is checking. */
export interface TypedDetails {
  readonly type: ItemType;
  readonly details: Readonly<Record<string, unknown>>;
}

/**
 * A check that validates `details` against the schema of the value's own
 * `type`, reporting each issue under the `details` path so a form shows a
 * bad VLAN tag or port row on the field itself. The item form and the
 * preset form record the same typed object, so they share this rule rather
 * than each restating it.
 */
export function detailsMatchType<T extends TypedDetails>() {
  return z.superRefine<T>((input, context) => {
    const result = z.safeParse(detailsSchemaFor(input.type), input.details);
    if (!result.success) {
      for (const issue of result.error.issues) {
        context.addIssue({ ...issue, path: ['details', ...issue.path] });
      }
    }
  });
}

export const extraSchema = z.object({
  key: z.string().check(z.trim(), z.minLength(1, 'Required'), z.maxLength(EXTRA_KEY_MAX_LENGTH)),
  value: z.string().check(z.maxLength(EXTRA_VALUE_MAX_LENGTH)),
});
export type Extra = z.infer<typeof extraSchema>;
export const extrasSchema = z.array(extraSchema).check(z.maxLength(EXTRAS_MAX_ENTRIES));
